import { FC } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Tooltip as UITooltip } from '@/components/ui';
import { ChartResponse } from '../types';
import { COLORS } from '../constants';
import { SkeletonChart } from './DashboardSkeletons';

interface DashboardChartProps {
    loading: boolean;
    chartData: ChartResponse | null;
}

export const DashboardChart: FC<DashboardChartProps> = ({ loading, chartData }) => {
    if (loading) return <SkeletonChart height="h-72" />;

    if (!chartData) return null;

    return (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-50 flex items-center justify-between">
                <UITooltip content="A detailed chart showing how your performance metrics change over time." position="right">
                    <div>
                        <h2 className="text-sm font-semibold text-slate-800">{chartData.title || 'Performance Overview'}</h2>
                        <p className="text-xs text-slate-400 mt-0.5">Time-based performance</p>
                    </div>
                </UITooltip>
                <div className="flex gap-4">
                    {chartData.series?.map((s, i) => (
                        <span key={s.key} className="flex items-center gap-1.5 text-[10px] font-medium text-slate-400">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            {s.label}
                        </span>
                    ))}
                </div>
            </div>
            <div className="p-6 h-96">
                {chartData.data?.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData.data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                {chartData.series.map((s, i) => (
                                    <linearGradient key={s.key} id={`g-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.15} />
                                        <stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} />
                                    </linearGradient>
                                ))}
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1e293b',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '11px',
                                    color: 'white'
                                }}
                            />
                            {chartData.series.map((s, i) => (
                                <Area
                                    key={s.key}
                                    type="monotone"
                                    dataKey={s.key}
                                    stroke={COLORS[i % COLORS.length]}
                                    strokeWidth={2}
                                    fill={`url(#g-${s.key})`}
                                    dot={false}
                                />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-full flex items-center justify-center text-xs text-slate-300">
                        No data available
                    </div>
                )}
            </div>
        </div>
    );
};
